import { useState, useEffect } from "react";
import { ModalHeader } from "react-bootstrap";
import { useMutation, useQuery } from "react-query";
import Select from "react-select";
import { toast } from "react-toastify";
import { Modal, ModalBody, ModalFooter, Button, Spinner } from "reactstrap";
import {
  postPromotionsAPI,
  getPromotionById,
  updatePromotion,
} from "../../MyApi/promotions";
import { getProducts } from "../../MyApi/product";
import { getCategories } from "../../MyApi/product/categories";
import { getStores } from "../../MyApi/stores";
import ButtonGlobal from "../../Components/UI/buttonGlobal";
import EnhancedSearchProduct from "../../Components/UI/enhancedproductsearch";
import i18next from "i18next";
import LangSelectModal from "../../Components/UI/langselectmodal";
import SkeletonWrapper from "../../Components/UI/skeletonWrapper";
import { getCountries } from "../../MyApi/admin/countries/countries";
import { formatPrice } from "../../utils/formatPrice";
import EnhancedSearchStore from "../../Components/UI/enhancedstoresearch";
import { getZones } from "../../MyApi/zones";
import { getCities } from "../../MyApi/admin/cities";
import { getGovernorates } from "../../MyApi/admin/governorates";
import { promotionsSechma } from "./sechma/promotionSechma";

const AddModalPromotion = ({
  isOpen,
  onClose,
  refetch,
  mode = "create",
  promotionId = null,
}) => {
  const isEdit = mode === "edit";
  const isShow = mode === "show";
  const lang = i18next.language;
  const [selectedLangCode, setSelectedLangCode] = useState(lang);
  const [formErrors, setFormErrors] = useState({});

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    type: "", // in
    sub_type: "",
    usage_limit: "",
    usage_limit_per_user: "",
    min_order_amount: "",
    max_order_amount: "",
    discount_value: "",
    fixed_delivery_price: "",
    start_date: "",
    end_date: "",
    is_active: 1,
    first_order_only: 0,
    country_id: "",
    currency_factor: 1,
    governorate_id: "",
    city_id: "",
    zone_id: "",
    selectedStores: [],
    selectedCategories: [],
    selectedProducts: [],
    selectedCities: [],
    selectedZones: [],
    targets: [],
  });

  const { data: promotionData, isLoading: promotionLoading } = useQuery(
    ["promotion", promotionId, selectedLangCode],
    () => getPromotionById(promotionId, selectedLangCode),
    {
      enabled: (isEdit || isShow) && !!promotionId,
    },
  );

  const { data: zonesData, isLoading: zonesLoading } = useQuery(
    [
      "getZones",
      formData.country_id,
      formData.governorate_id,
      formData.city_id,
    ],
    () => getZones(i18next.language, formData.city_id),
  );
  const { data: cityData, isLoading: cityLoading } = useQuery(
    ["getCities", formData.country_id, formData.governorate_id],
    () => getCities(i18next.language, "", formData.governorate_id),
  );
  const { data: governoratesData, isLoading: governorateLoading } = useQuery(
    ["getGovernorates", formData.country_id],
    () => getGovernorates(formData.country_id, i18next.language),
  );
  const { data: categoriesDats } = useQuery("getCategories", () =>
    getCategories(),
  );

  const categories = categoriesDats?.data?.categories || [];

  const [promotionScope, setPromotionScope] = useState("stores"); // in "products", "stores", or "categories"

  const mutation = useMutation({
    mutationKey: ["postPromotion"],
    mutationFn: (postData) => postPromotionsAPI(postData),
    onSuccess: (data) => {
      toast.success(`${data.message}`);
      onClose();
      refetch();
    },
    onError: (error) => {
      toast.error(`${error.response.data.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationKey: ["updatePromotion"],
    mutationFn: (postData) => updatePromotion(promotionId, postData),
    onSuccess: (data) => {
      toast.success(`${data.message}`);
      onClose();
      refetch();
    },
    onError: (error) => {
      toast.error(`${error.response.data.message}`);
    },
  });

  useEffect(() => {
    if ((isEdit || isShow) && promotionData?.data?.promotion) {
      const promotion = promotionData.data.promotion;
      setFormData({
        title: promotion.title || "",
        description: promotion.description || "",
        type: promotion.type || "",
        sub_type: promotion.sub_type || "",
        usage_limit: promotion.usage_limit || "",
        usage_limit_per_user: promotion.usage_limit_per_user || "",
        min_order_amount:
          formatPrice(promotion.currency_factor, promotion.min_order_amount) ||
          "",
        max_order_amount:
          formatPrice(promotion.currency_factor, promotion.max_order_amount) ||
          "",
        discount_value:
          formatPrice(promotion.currency_factor, promotion.discount_value) ||
          "",
        fixed_delivery_price: promotion.fixed_delivery_price || "",
        start_date: promotion.start_date
          ? promotion.start_date.split("T")[0]
          : "",
        end_date: promotion.end_date ? promotion.end_date.split("T")[0] : "",
        is_active: Number(promotion.is_active),
first_order_only: Number(promotion.first_order_only),
        country_id: promotion.country_id || "",
        currency_factor: promotion.currency_factor || 1,
        governorate_id: promotion.governorate_id || "",
        city_id: promotion.city_id || "",
        zone_id: promotion.zone_id || "",
        selectedStores:
          promotion.targets
            ?.filter((target) => target.target_type === "store")
            .map((target) => ({
              value: target.target.id,
              label: target.target.name || `Store ${target.target_id}`,
            })) || [],
        selectedCategories:
          promotion.targets
            ?.filter((target) => target.target_type === "category")
            .map((target) => ({
              value: target.target.id,
              label: target.target.name || `Category ${target.target_id}`,
            })) || [],
        selectedProducts:
          promotion.targets
            ?.filter((target) => target.target_type === "product")
            .map((target) => ({
              value: target.target.id,
              label: target.target.name || `Product ${target.target_id}`,
            })) || [],
        selectedCities: promotion.cities || [],
        selectedZones: promotion.zones || [],
      });

      if (
        promotion.targets?.some((target) => target.target_type === "product")
      ) {
        setPromotionScope("products");
      } else if (
        promotion.targets?.some((target) => target.target_type === "store")
      ) {
        setPromotionScope("stores");
      } else if (
        promotion.targets?.some((target) => target.target_type === "category")
      ) {
        setPromotionScope("categories");
      }
    } else if (mode === "create") {
      // Reset form
      setFormData({
        title: "",
        description: "",
        type: "",
        sub_type: "",
        usage_limit: "",
        usage_limit_per_user: "",
        min_order_amount: "",
        max_order_amount: "",
        discount_value: "",
        fixed_delivery_price: "",
        start_date: "",
        end_date: "",
        is_active: 1,
        first_order_only: 0,
        country_id: "",
        currency_factor: 1,
        selectedStores: [],
        selectedCategories: [],
        selectedProducts: [],
        targets: [],
      });

      // Reset scope and type
      setPromotionScope("");
    }
  }, [isEdit, isShow, promotionData, isOpen, mode]);

  console.log("promotionScope", promotionScope);

  useEffect(() => {
      if (mode !== "create") return; // ← skip for edit/show

    if (formData.sub_type === "first_order") {
      setFormData((prev) => ({ ...prev, first_order_only: 1 }));
    } else {
      setFormData((prev) => ({ ...prev, first_order_only: 0 }));
    }
  }, [formData.sub_type]);
  console.log("formData.sub_type", formData.sub_type);
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (checked ? 1 : 0) : value,
    }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };
  console.log("formErrors", formErrors);
  const handleSelectChange = (field, selectedOptions) => {
    console.log("selectedOptions", selectedOptions);
    setFormData((prev) => ({
      ...prev,
      [field]: selectedOptions,
    }));
  };

  const handleChangeLanguage = (e) => {
    setSelectedLangCode(e.target.value);
  };
  const mapZodErrors = (zodError) => {
    const error = {};
    zodError.forEach((err) => {
      const field = err.path[0];
      error[field] = err.message;
    });
    return error;
  };

  const handleSubmit = async () => {
    const submitData = new FormData();
    submitData.append("title", formData.title);
    submitData.append("description", formData.description);
    submitData.append("type", formData.type);
    submitData.append("sub_type", formData.sub_type);
    submitData.append("usage_limit", formData.usage_limit);
    submitData.append("usage_limit_per_user", formData.usage_limit_per_user);
    submitData.append("min_order_amount", formData.min_order_amount);
    submitData.append("max_order_amount", formData.max_order_amount);

    const formatAPIDate = (dateStr) => {
      if (!dateStr) return "";
      return `${dateStr}T22:00:00.000000Z`;
    };

    submitData.append("start_date", formatAPIDate(formData.start_date));
    submitData.append("end_date", formatAPIDate(formData.end_date));
    submitData.append("is_active", formData.is_active ? 1 : 0);
    submitData.append("first_order_only", formData.first_order_only ? 1 : 0);
    submitData.append("country_id", formData.country_id);
    submitData.append("currency_factor", formData.currency_factor);

    // Add discount or delivery specific fields
    if (formData.type !== "free_delivery") {
      submitData.append("discount_value", formData.discount_value);
    }

    // Build targets array
    const targets = [];

    formData.selectedProducts.forEach((product) => {
      targets.push({
        target_type: "product",
        target_id: product.value,
        is_excluded: false,
      });
    });

    formData.selectedStores.forEach((store) => {
      targets.push({
        target_type: "store",
        target_id: store.value,
        is_excluded: false,
      });
    });

    formData.selectedCategories.forEach((category) => {
      targets.push({
        target_type: "category",
        target_id: category.value,
        is_excluded: false,
      });
    });

    // Add targets to form data
    targets.forEach((target, index) => {
      submitData.append(`targets[${index}][target_type]`, target.target_type);
      submitData.append(`targets[${index}][target_id]`, target.target_id);
      submitData.append(
        `targets[${index}][is_excluded]`,
        target.is_excluded ? 1 : 0,
      );
    });

    // Add location filters
    if (formData.country_id) {
      submitData.append("country_id", formData.country_id);
    }
    if (formData.governorate_id) {
      submitData.append("governorate_id", formData.governorate_id);
    }
    if (formData.city_id) {
      submitData.append("city_id", formData.city_id);
    }
    if (formData.zone_id) {
      submitData.append("zone_id", formData.zone_id);
    }

    if (isEdit) {
      submitData.append("lang", selectedLangCode);
      submitData.append("_method", "PUT");
      updateMutation.mutate(submitData);
    } else {
      const result = promotionsSechma.safeParse({ ...formData });

      if (result.success) {
        mutation.mutate(submitData);
      } else {
        const errors = mapZodErrors(result.error.issues);

        setFormErrors(errors);
      }
    }
  };
  console.log("fromErrors", formErrors);
  const modalTitle = isShow
    ? "عرض العرض الترويجي"
    : isEdit
      ? "تعديل العرض الترويجي"
      : "إضافة عرض ترويجي جديد";
  const buttonText = isEdit ? "تحديث" : "حفظ";
  const countriesQuery = useQuery("getCountries", getCountries);
  const countries = countriesQuery?.data?.data?.countries || [];

  const handleChangeCountryId = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      country_id: selectedOption?.id,
      currency_factor: selectedOption?.currency_factor,
    }));
  };

  const sub_types = [
    { value: "percentage_discount", label: "خصم نسبة مئوية" },
    { value: "fixed_price", label: "سعر ثابت" },
    { value: "first_order", label: "اول طلب" },
    { value: "discount_delivery", label: "خصم علي التوصيل" },
    { value: "free_delivery", label: "توصيل مجاني" },
  ];
  const zones = zonesData?.data?.zones || [];
  return (
    <Modal
      isOpen={isOpen}
      centered
      backdrop={true}
      size="lg"
      className="add"
      toggle={onClose}
    >
      <ModalHeader className="fs-5">{modalTitle}</ModalHeader>

      <ModalBody>
        {isEdit && (
          <LangSelectModal
            handleChange={handleChangeLanguage}
            selectedLangCode={selectedLangCode}
          />
        )}
        <div className="row add">
          <div className="col-md-3">
            <label htmlFor="" className="form-label">
              الدول
            </label>
            <Select
              placeholder="اختر الدولة"
              isDisabled={isShow}
              isLoading={countriesQuery.isLoading}
              options={countries}
              getOptionLabel={(e) => e.name}
              getOptionValue={(e) => e.id}
              onChange={handleChangeCountryId}
              value={countries.find((c) => c.id === formData.country_id)}
              isClearable
            />
          </div>

          <div className="col-md-3">
            <label htmlFor="" className="form-label">
              المحافظة
            </label>
            <Select
              placeholder="اختر المحافظة"
              isDisabled={isShow}
              isLoading={governorateLoading}
              options={governoratesData?.data?.governorates || []}
              getOptionLabel={(e) => e.name}
              getOptionValue={(e) => e.id}
              onChange={(selected) => {
                setFormData((prev) => ({
                  ...prev,
                  governorate_id: selected?.id,
                  city_id: "",
                  selectedCities: [],
                }));
              }}
              value={governoratesData?.data?.data?.find(
                (g) => g.id === formData.governorate_id,
              )}
              isClearable
            />
          </div>

          <div className="col-md-3">
            <label htmlFor="" className="form-label">
              المدينة
            </label>
            <Select
              placeholder="اختر المدينة"
              isDisabled={isShow}
              isLoading={cityLoading}
              options={cityData?.data?.cities || []}
              getOptionLabel={(e) => e.name}
              getOptionValue={(e) => e.id}
              onChange={(selected) => {
                setFormData((prev) => ({
                  ...prev,
                  city_id: selected?.id,
                  selectedCities: selected ? [selected] : [],
                }));
              }}
              value={cityData?.data?.cities?.find(
                (c) => c.id === formData.city_id,
              )}
              isClearable
            />
          </div>

          <div className="col-md-3">
            <label htmlFor="" className="form-label">
              المنطقة
            </label>
            <Select
              placeholder="اختر المنطقة"
              isDisabled={isShow}
              isLoading={zonesLoading}
              options={zones}
              getOptionLabel={(e) => e.name}
              getOptionValue={(e) => e.id}
              onChange={(selected) => {
                setFormData((prev) => ({
                  ...prev,
                  zone_id: selected?.id,
                  selectedZones: selected ? [selected] : [],
                }));
              }}
              value={zones.find((z) => z.id === formData.zone_id)}
              isClearable
            />
          </div>

          <div className="col-md-6 mt-2">
            <label className="form-label">الاسم</label>
            <input
              type="text"
              name="title"
              placeholder="أدخل اسم العرض"
              className="form-control"
              value={formData.title}
              onChange={handleChange}
              disabled={isShow}
            />
            {formErrors?.title && (
              <small className="d-block invalid-feedback ">
                {formErrors?.title}
              </small>
            )}
          </div>

          <div className="col-md-6 mt-2">
            <label className="form-label">الوصف</label>
            <input
              type="text"
              name="description"
              placeholder="أدخل وصف العرض"
              className="form-control"
              value={formData.description}
              onChange={handleChange}
              disabled={isShow}
            />
          </div>

          <div className="col-md-6 mt-2">
            <label className="form-label">نوع العرض</label>
            <select
              className="form-select"
              name="type"
              value={formData.type}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, type: e.target.value }));
                setFormErrors((prev) => ({ ...prev, type: "" }));
              }}
              disabled={isShow}
            >
              <option> اختار نوع العرض</option>
              <option value="user">مستخدم</option>
              <option value="delivery">توصيل</option>
              <option value="product">منتج</option>
              <option value="order">طلب</option>
            </select>
            {formErrors?.type && (
              <small className="d-block invalid-feedback ">
                {formErrors?.type}
              </small>
            )}
          </div>

          <div className="col-md-6 mt-2">
            <label className="form-label">النوع الفرعي</label>
            <select
              className="form-select"
              name="sub_type"
              value={formData.sub_type}
              onChange={handleChange}
              disabled={isShow}
            >
              <option value="">اختر النوع الفرعي</option>
              {sub_types.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {formErrors?.sub_type && (
              <small className="d-block invalid-feedback ">
                {formErrors?.sub_type}
              </small>
            )}
          </div>

          <div className="col-md-6">
            <label className="form-label">عدد الاستخدامات المسموح بها</label>
            <input
              type="number"
              name="usage_limit"
              min={0}
              className="form-control"
              placeholder="عدد الاستخدامات"
              value={formData.usage_limit}
              onChange={handleChange}
              disabled={isShow}
            />
          </div>

          <div className="col-md-6 mt-2">
            <label className="form-label">
              عدد الاستخدامات المسموح لكل مستخدم
            </label>
            <input
              type="number"
              name="usage_limit_per_user"
              min={0}
              className="form-control"
              placeholder="عدد الاستخدامات لكل مستخدم"
              value={formData.usage_limit_per_user}
              onChange={handleChange}
              disabled={isShow}
            />
            {formErrors?.usage_limit_per_user && (
              <small className="d-block invalid-feedback ">
                {formErrors?.usage_limit_per_user}
              </small>
            )}
          </div>

          <div className="col-md-6 mt-2">
            <label className="form-label">الحد الأدنى للطلب</label>
            <input
              type="number"
              name="min_order_amount"
              min={0}
              className="form-control"
              placeholder="الحد الأدنى للطلب"
              value={formData.min_order_amount}
              onChange={handleChange}
              disabled={isShow}
            />
          </div>

          <div className="col-md-6 mt-2">
            <label className="form-label">الحد الأقصى للطلب</label>
            <input
              type="number"
              name="max_order_amount"
              min={0}
              className="form-control"
              placeholder="الحد الأقصى للطلب"
              value={formData.max_order_amount}
              onChange={handleChange}
              disabled={isShow}
            />
          </div>

          {formData.sub_type !== "free_delivery" && (
            <div className="col-12 mt-2">
              <label className="form-label">قيمة الخصم</label>
              <input
                type="number"
                name="discount_value"
                min={0}
                className="form-control"
                placeholder={
                  formData.sub_type === "percentage_discount"
                    ? "نسبة الخصم %"
                    : "قيمة الخصم"
                }
                value={formData.discount_value}
                onChange={handleChange}
                disabled={isShow}
              />
            </div>
          )}

          {formData.sub_type === "fixed_price" && (
            <div className="col-md-6 mt-2">
              <label className="form-label">سعر التوصيل الثابت</label>
              <input
                type="number"
                name="fixed_delivery_price"
                min={0}
                className="form-control"
                placeholder="سعر التوصيل الثابت"
                value={formData.fixed_delivery_price}
                onChange={handleChange}
                disabled={isShow}
              />
            </div>
          )}

          <div className="col-md-6">
            <label className="form-label">تاريخ البداية</label>
            <input
              type="date"
              name="start_date"
              className="form-control"
              value={formData.start_date}
              onChange={handleChange}
              disabled={isShow}
            />
            {formErrors?.start_date && (
              <small className="d-block invalid-feedback ">
                {formErrors?.start_date}
              </small>
            )}
          </div>
          <div className="col-md-6 ">
            <label className="form-label">تاريخ النهاية</label>
            <input
              type="date"
              name="end_date"
              className="form-control"
              value={formData.end_date}
              onChange={handleChange}
              disabled={isShow}
            />
          </div>

          <div className="col-12 mt-2">
            <label className="form-label mb-3">تطبيق العرض على:</label>

            <div className="d-flex flex-column gap-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="promotionScope"
                  id="scopeProducts"
                  value="products"
                  checked={promotionScope === "products"}
                  onChange={(e) => {
                    setPromotionScope(e.target.value);
                    // Clear other selections
                    setFormData((prev) => ({
                      ...prev,
                      selectedStores: [],
                      selectedCategories: [],
                    }));
                  }}
                  disabled={isShow}
                />
                <label className="form-check-label" htmlFor="scopeProducts">
                  المنتجات
                </label>
              </div>

              {promotionScope === "products" && (
                <SkeletonWrapper
                  isLoading={(isEdit || isShow) && promotionLoading}
                >
                  <EnhancedSearchProduct
                    isMulti={true}
                    value={
                      mode === "edit" || mode === "show"
                        ? formData.selectedProducts.map((p) => ({
                            id: p.value,
                            name: p.label,
                          }))
                        : null
                    }
                    setProductId={(productId) => {
                      if (productId) {
                        setFormData((prev) => ({
                          ...prev,
                          selectedProducts: productId.map((product) => ({
                            value: product.id,
                            label: product.name,
                          })),
                        }));
                      }
                    }}
                    disabled={isShow}
                  />
                </SkeletonWrapper>
              )}

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="promotionScope"
                  id="scopeStores"
                  value="stores"
                  checked={promotionScope === "stores"}
                  onChange={(e) => {
                    setPromotionScope(e.target.value);
                    // Clear other selections
                    setFormData((prev) => ({
                      ...prev,
                      selectedProducts: [],
                      selectedCategories: [],
                    }));
                  }}
                  disabled={isShow}
                />
                <label className="form-check-label" htmlFor="scopeStores">
                  المتاجر
                </label>
              </div>

              {promotionScope === "stores" && (
                <SkeletonWrapper
                  isLoading={(isEdit || isShow) && promotionLoading}
                >
                  <EnhancedSearchStore
                    isMulti={true}
                    value={
                      mode === "edit" || mode === "show"
                        ? formData.selectedStores.map((p) => ({
                            id: p.value,
                            name: p.label,
                          }))
                        : null
                    }
                    setStoreId={(storeId) => {
                      if (storeId) {
                        setFormData((prev) => ({
                          ...prev,
                          selectedStores: storeId.map((store) => ({
                            value: store.id,
                            label: store.name,
                          })),
                        }));
                      }
                    }}
                    placeholder="اختر المتاجر"
                    isDisabled={isShow}
                  />
                </SkeletonWrapper>
              )}

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="promotionScope"
                  id="scopeCategories"
                  value="categories"
                  checked={promotionScope === "categories"}
                  onChange={(e) => {
                    setPromotionScope(e.target.value);
                    // Clear other selections
                    setFormData((prev) => ({
                      ...prev,
                      selectedProducts: [],
                      selectedStores: [],
                    }));
                  }}
                  disabled={isShow}
                />
                <label className="form-check-label" htmlFor="scopeCategories">
                  الفئات
                </label>
              </div>

              {promotionScope === "categories" && (
                <SkeletonWrapper
                  isLoading={(isEdit || isShow) && promotionLoading}
                >
                  <Select
                    isMulti
                    options={categories.map((s) => ({
                      value: s.id,
                      label: s.name,
                    }))}
                    value={formData.selectedCategories}
                    onChange={(selected) =>
                      handleSelectChange("selectedCategories", selected)
                    }
                    placeholder="اختر الفئات"
                    isDisabled={isShow}
                  />
                </SkeletonWrapper>
              )}
            </div>
          </div>

          <div className="col-12 mt-2">
            <div>
              <label htmlFor="">الطلب الأول فقط</label>
              <label class="switch">
                <input
                  type="checkbox"
                  name="first_order_only"
                  onChange={handleChange}
                  checked={formData.first_order_only === 1}
                  disabled={isShow || formData.sub_type === "first_order"}
                />
                <span className="slider round"></span>
              </label>
            </div>
          </div>

          <div className="col-12">
            <div>
              <label htmlFor="">نشط</label>
              <label class="switch">
                <input
                  type="checkbox"
                  name="is_active"
                  onChange={handleChange}
                  checked={formData.is_active === 1}
                />
                <span className="slider round"></span>
              </label>
            </div>
          </div>
        </div>
      </ModalBody>

      {isShow ? null : (
        <ModalFooter>
          <div className="col-3">
            <ButtonGlobal
              className="btn-primary"
              onClick={handleSubmit}
              label={buttonText}
              isLoading={isEdit ? updateMutation.isLoading : mutation.isLoading}
              disabled={isEdit ? updateMutation.isLoading : mutation.isLoading}
            />
          </div>
          <button className="btn btn-white" type="button" onClick={onClose}>
            إلغاء
          </button>
        </ModalFooter>
      )}
    </Modal>
  );
};

export default AddModalPromotion;
